import React from 'react';
import { Button, Badge, TextInput, Card } from '@parsina/framework';
import {
  TriangleAlert, User, Activity, Pill, Stethoscope, History, FlaskConical, NotebookPen,
} from 'lucide-react';

import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import RecordSection, { ViewAllButton } from '../../../components/common/RecordSection.jsx';
import RailListSheet from '../../../components/clinical/RailListSheet.jsx';
import { PracticeBadge } from '../../../components/common/PracticeNotice.jsx';
import { sexKey, sectionKey } from '../../../constants/vocabulary.js';
import { entriesOf, hasAllergies, writtenSections } from '../Visit.shared.js';

// پرونده — what is already known about this patient, beside what is being
// written about them now. Read-only, collapsible, in the fixed order of §7.5:
//
//   هشدار حساسیت (locked, first, only when there is one) · مشخصات · علائم حیاتی ·
//   داروهای فعلی · مشکلات فعال · سابقهٔ بیماری · آزمایش‌ها · شرح حال
//
// ── The allergy block cannot be collapsed ────────────────────────────────
//
// `RecordSection`'s `locked` exists for this and for nothing else. A safety
// notice a student can fold away is one they fold away once, on the first visit,
// and never see again — and the visit where it mattered is the one where they
// were busy.
//
// ── One editable thing, and it writes to the CASE ────────────────────────
//
// «ثبت» beside علائم حیاتی adds a `vitals` history entry STAMPED with this
// encounter (core.md §7.5). It is a history entry rather than an encounter row
// on purpose: a measurement survives the visit being cancelled (§5.1), and the
// timeline groups it under «ویزیت N» because of the stamp, not because of where
// it is stored.

// Three rows per block; «مشاهدهٔ همه» is offered whenever there is ANY row, not
// only when a fourth exists — the sheet is also where a long entry's full text
// is readable, and the rail clips those at one line each.
const CLIP = 3;

const Line = ({ children, muted }) => (
  <p style={{
    margin: '0 0 4px', fontSize: 12.5, lineHeight: 1.85,
    color: muted ? CT.mute : C.textPrimary,
  }}
  >
    {children}
  </p>
);

/** The «ثبت» quick-add: a one-line vitals entry, nothing more. */
function VitalsQuickAdd({ onAdd, disabled }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  if (disabled) return null;
  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="rail-vitals-add">
        {t('visit.rail.vitalsAdd')}
      </Button>
    );
  }
  return (
    <Card style={{ padding: 8, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <TextInput
        value={text}
        placeholder={t('visit.rail.vitalsPlaceholder')}
        onChange={(e) => setText(e.target.value)}
        data-testid="rail-vitals-input"
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <Button
          size="sm"
          disabled={saving || !text.trim()}
          onClick={async () => {
            setSaving(true);
            try {
              await onAdd({ body: text.trim() });
              setText('');
              setOpen(false);
            } finally { setSaving(false); }
          }}
          data-testid="rail-vitals-save"
        >
          {t('common.actions.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setText(''); }}>
          {t('common.actions.cancel')}
        </Button>
      </div>
    </Card>
  );
}

export default function RecordRail({ record, onAddVitals, readOnly = false }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const [sheet, setSheet] = React.useState(null); // { title, items, renderItem }

  const caseRow = record?.case || null;
  const allergies = entriesOf(record, 'allergies');
  const vitals = entriesOf(record, 'vitals');
  const pmh = entriesOf(record, 'pmh');
  const labs = entriesOf(record, 'labs');
  const problems = record?.activeProblems || [];
  const meds = record?.currentMedications || [];

  const openSheet = (title, items, renderItem) => setSheet({ title, items, renderItem });

  const entryText = (e) => (
    <>
      {e.label && <strong style={{ display: 'block', fontSize: 12 }}>{e.label}</strong>}
      <span style={{ fontSize: 12.5, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{e.body}</span>
      {(e.recordedAtMs || e.createdAtMs) && (
        <span style={{ display: 'block', fontSize: 11, color: CT.mute, marginTop: 3 }}>
          {formatDate(Number(e.recordedAtMs || e.createdAtMs))}
        </span>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="visit-rail">
      {hasAllergies(record) && (
        <RecordSection
          id="visit-allergies"
          title={t('visit.rail.allergies')}
          icon={TriangleAlert}
          locked
          danger
          count={allergies.length}
          data-testid="rail-allergies"
        >
          {allergies.slice(0, CLIP).map((e) => <Line key={e.id}>{e.body}</Line>)}
          {allergies.length > 0 && (
            <ViewAllButton
              onClick={() => openSheet(t('visit.rail.allergies'), allergies, entryText)}
              testId="rail-allergies-all"
            />
          )}
        </RecordSection>
      )}

      <RecordSection id="visit-identity" title={t('visit.rail.identity')} icon={User}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          <strong style={{ fontSize: 13 }}>{caseRow?.displayName || caseRow?.title || ''}</strong>
          <PracticeBadge short />
        </div>
        <Line muted>
          {[
            caseRow?.ageDisplay || (caseRow?.ageYears ? t('visit.rail.ageYears', { n: formatNumber(caseRow.ageYears) }) : ''),
            caseRow?.sex ? t(sexKey(caseRow.sex) || 'common.gender.other') : '',
            caseRow?.bloodType || '',
          ].filter(Boolean).join(' · ')}
        </Line>
        {caseRow?.chiefComplaint && <Line>{caseRow.chiefComplaint}</Line>}
      </RecordSection>

      <RecordSection
        id="visit-vitals"
        title={t('visit.rail.vitals')}
        icon={Activity}
        count={vitals.length}
        action={vitals.length > 0
          ? <ViewAllButton onClick={() => openSheet(t('visit.rail.vitals'), vitals, entryText)} testId="rail-vitals-all" />
          : null}
      >
        {vitals.slice(0, CLIP).map((e) => <Line key={e.id}>{e.body}</Line>)}
        {!vitals.length && <Line muted>{t('visit.rail.noVitals')}</Line>}
        <VitalsQuickAdd onAdd={onAddVitals} disabled={readOnly} />
      </RecordSection>

      <RecordSection id="visit-meds" title={t('visit.rail.medications')} icon={Pill} count={meds.length}>
        {meds.slice(0, CLIP).map((m, i) => (
          <Line key={`${m.name}-${i}`}>
            {m.name}
            {m.detail ? ` — ${m.detail}` : ''}
          </Line>
        ))}
        {!meds.length && <Line muted>{t('visit.rail.noMedications')}</Line>}
        {meds.length > 0 && (
          <ViewAllButton
            onClick={() => openSheet(
              t('visit.rail.medications'),
              meds.map((m, i) => ({ id: `${m.name}-${i}`, title: `${m.name}${m.detail ? ` — ${m.detail}` : ''}` })),
            )}
            testId="rail-meds-all"
          />
        )}
      </RecordSection>

      <RecordSection id="visit-problems" title={t('visit.rail.problems')} icon={Stethoscope} count={problems.length}>
        {problems.slice(0, CLIP).map((p, i) => (
          <Line key={`${p.title}-${i}`}>
            {p.title}
            {p.icdCode && (
              <span dir="ltr" style={{ color: CT.mute, marginInlineStart: 6, unicodeBidi: 'isolate' }}>
                {p.icdCode}
              </span>
            )}
          </Line>
        ))}
        {!problems.length && <Line muted>{t('visit.rail.noProblems')}</Line>}
        {problems.length > 0 && (
          <ViewAllButton
            onClick={() => openSheet(
              t('visit.rail.problems'),
              problems.map((p, i) => ({ id: `${p.title}-${i}`, title: p.title })),
            )}
            testId="rail-problems-all"
          />
        )}
      </RecordSection>

      <RecordSection id="visit-pmh" title={t('visit.rail.pmh')} icon={History} count={pmh.length} defaultOpen={false}>
        {pmh.slice(0, CLIP).map((e) => <Line key={e.id}>{e.body}</Line>)}
        {!pmh.length && <Line muted>{t('visit.rail.noPmh')}</Line>}
        {pmh.length > 0 && (
          <ViewAllButton onClick={() => openSheet(t('visit.rail.pmh'), pmh, entryText)} testId="rail-pmh-all" />
        )}
      </RecordSection>

      <RecordSection id="visit-labs" title={t('visit.rail.labs')} icon={FlaskConical} count={labs.length} defaultOpen={false}>
        {labs.slice(0, CLIP).map((e) => <Line key={e.id}>{e.body}</Line>)}
        {!labs.length && <Line muted>{t('visit.rail.noLabs')}</Line>}
        {labs.length > 0 && (
          <ViewAllButton onClick={() => openSheet(t('visit.rail.labs'), labs, entryText)} testId="rail-labs-all" />
        )}
      </RecordSection>

      <RecordSection id="visit-history" title={t('visit.rail.history')} icon={NotebookPen} defaultOpen={false}>
        {writtenSections(record).map((section) => {
          const rows = entriesOf(record, section);
          return (
            <div key={section} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong style={{ fontSize: 12, color: CT.ink2 }}>{t(sectionKey(section))}</strong>
                <Badge variant="outline">{formatNumber(rows.length)}</Badge>
                <span style={{ marginInlineStart: 'auto' }}>
                  <ViewAllButton
                    onClick={() => openSheet(t(sectionKey(section)), rows, entryText)}
                    testId={`rail-history-all-${section}`}
                  />
                </span>
              </div>
              <Line muted>{rows[0]?.body}</Line>
            </div>
          );
        })}
        {!writtenSections(record).length && <Line muted>{t('visit.rail.noHistory')}</Line>}
      </RecordSection>

      <RailListSheet
        open={!!sheet}
        onClose={() => setSheet(null)}
        title={sheet?.title}
        items={sheet?.items || []}
        renderItem={sheet?.renderItem}
      />
    </div>
  );
}
